import { useEffect, useRef } from 'react';
import './ContourField.css';

// A slowly shifting topographic map drawn on a canvas. Contour lines come from a
// noise field (marching squares); every fifth line is heavier, like the index
// contours on a survey map. The cursor pushes a gentle rise into the terrain.
// Given `sections`, the map follows the page: it slides by slower than the content,
// reshapes as you scroll, and each section has its own kind of terrain.

// Ken Perlin's improved noise (3D). Fixed seed, so the map always starts the same.
const PERM = new Uint8Array(512);
{
  const p = Array.from({ length: 256 }, (_, i) => i);
  let seed = 1984;
  for (let i = 255; i > 0; i -= 1) {
    seed = (seed * 16807) % 2147483647;
    const j = seed % (i + 1);
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 512; i += 1) PERM[i] = p[i & 255];
}

const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
const mix = (a, b, t) => a + t * (b - a);
const grad = (hash, x, y, z) => {
  const h = hash & 15;
  const u = h < 8 ? x : y;
  const v = h < 4 ? y : h === 12 || h === 14 ? x : z;
  return ((h & 1) ? -u : u) + ((h & 2) ? -v : v);
};

function noise(x, y, z) {
  const X = Math.floor(x) & 255;
  const Y = Math.floor(y) & 255;
  const Z = Math.floor(z) & 255;
  x -= Math.floor(x);
  y -= Math.floor(y);
  z -= Math.floor(z);
  const u = fade(x);
  const v = fade(y);
  const w = fade(z);
  const A = PERM[X] + Y;
  const AA = PERM[A] + Z;
  const AB = PERM[A + 1] + Z;
  const B = PERM[X + 1] + Y;
  const BA = PERM[B] + Z;
  const BB = PERM[B + 1] + Z;
  return mix(
    mix(mix(grad(PERM[AA], x, y, z), grad(PERM[BA], x - 1, y, z), u),
      mix(grad(PERM[AB], x, y - 1, z), grad(PERM[BB], x - 1, y - 1, z), u), v),
    mix(mix(grad(PERM[AA + 1], x, y, z - 1), grad(PERM[BA + 1], x - 1, y, z - 1), u),
      mix(grad(PERM[AB + 1], x, y - 1, z - 1), grad(PERM[BB + 1], x - 1, y - 1, z - 1), u), v),
    w,
  );
}

// Marching squares: which cell edges a contour crosses, for each corner pattern.
// Corners are top-left 8, top-right 4, bottom-right 2, bottom-left 1; edges T R B L.
const SEGMENTS = [
  [], ['L', 'B'], ['B', 'R'], ['L', 'R'], ['T', 'R'], ['T', 'R', 'L', 'B'], ['T', 'B'], ['T', 'L'],
  ['T', 'L'], ['T', 'B'], ['T', 'L', 'B', 'R'], ['T', 'R'], ['L', 'R'], ['B', 'R'], ['L', 'B'], [],
];

const TONES = {
  light: { line: 'rgba(7, 55, 106, 0.11)', index: 'rgba(7, 55, 106, 0.22)' },
  dark: { line: 'rgba(185, 211, 238, 0.10)', index: 'rgba(185, 211, 238, 0.2)' },
};

const CELL = 14;           // grid spacing in CSS pixels
const SCALE = 0.0034;      // how zoomed in the terrain is
const LEVELS = 18;         // contour lines across the height range
const LOW = -0.8;          // height of the lowest line
const STEP = 1.6 / LEVELS;
const DRIFT = 0.000045;    // how fast the terrain reshapes on its own (per ms)
const IDLE_MS = 1000 / 30; // frame budget while only drifting
const BUSY_MS = 1000 / 70; // while scrolling or following the cursor: every frame, up to ~60fps
const SUMMIT = 0.78;       // height of the rise under the cursor
const RING_SPREAD = 210;   // larger = rings further apart
const PARALLAX = 0.35;     // the map slides by at this fraction of the scroll speed
const MORPH = 0.00028;     // how far the terrain reshapes per pixel scrolled
const EASE = 0.07;         // how quickly the terrain settles into a new section's shape

// zoom > 1: smaller, busier hills. stretch > 1: hills pulled sideways into long ridges.
// relief > 1: steeper, so lines bunch up. ink: line strength.
const PLAIN = { zoom: 1, stretch: 1, relief: 1, ink: 1 };
const KEYS = Object.keys(PLAIN);

const smooth = (t) => t * t * (3 - 2 * t);

// Where the contour at `level` crosses one edge of the cell at (x, y)
function crossing(edge, x, y, level, a, b, c, d) {
  switch (edge) {
    case 'T': return [x + CELL * ((level - a) / (b - a)), y];
    case 'R': return [x + CELL, y + CELL * ((level - b) / (c - b))];
    case 'B': return [x + CELL * ((level - d) / (c - d)), y + CELL];
    default: return [x, y + CELL * ((level - a) / (d - a))];
  }
}

export default function ContourField({ tone = 'light', interactive = true, sections = null, className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const colours = TONES[tone] || TONES.light;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const follow = Boolean(sections?.length) && !still;

    let width = 0;
    let height = 0;
    let cols = 0;
    let rows = 0;
    let field = new Float32Array(0);
    let frame = 0;
    let last = 0;
    let visible = true;
    let time = Math.random() * 100;
    let scrollY = 0;
    let reshaping = false;
    // Each section's terrain, anchored at the section's middle (page coordinates)
    let anchors = [];
    const view = { ...PLAIN };
    // The rise under the cursor eases towards where the pointer is, and away when it leaves
    const pointer = { x: 0, y: 0, tx: 0, ty: 0, strength: 0, target: 0 };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / CELL) + 1;
      rows = Math.ceil(height / CELL) + 1;
      field = new Float32Array(cols * rows);
    };

    const measure = () => {
      if (!follow) return;
      anchors = sections
        .map((section) => {
          const el = document.getElementById(section.id);
          if (!el) return null;
          const rect = el.getBoundingClientRect();
          return { ...PLAIN, ...section, at: rect.top + window.scrollY + rect.height / 2 };
        })
        .filter(Boolean)
        .sort((a, b) => a.at - b.at);
    };

    // The terrain for the middle of the screen: a blend of the two nearest sections
    const target = () => {
      if (!anchors.length) return PLAIN;
      const middle = scrollY + height / 2;
      if (middle <= anchors[0].at) return anchors[0];
      for (let i = 1; i < anchors.length; i += 1) {
        const from = anchors[i - 1];
        const to = anchors[i];
        if (middle < to.at) {
          const t = smooth((middle - from.at) / (to.at - from.at));
          return Object.fromEntries(KEYS.map((key) => [key, mix(from[key], to[key], t)]));
        }
      }
      return anchors[anchors.length - 1];
    };

    // Moves the view a step towards the target; true while it is still on its way
    const settle = (instant) => {
      const goal = target();
      let moving = false;
      KEYS.forEach((key) => {
        const gap = goal[key] - view[key];
        if (Math.abs(gap) > 0.0005) moving = true;
        view[key] += instant ? gap : gap * EASE;
      });
      return moving;
    };

    const sample = () => {
      const radius2 = 2 * 120 * 120;
      const cx = width / 2;
      const cy = height / 2;
      // Zoom and stretch work around the middle of the screen; scrolling slides the map up
      const fx = SCALE * view.zoom / view.stretch;
      const fy = SCALE * view.zoom;
      const shift = scrollY * PARALLAX * SCALE;
      const z = time + scrollY * MORPH;
      for (let j = 0; j < rows; j += 1) {
        const y = j * CELL;
        const ny = (y - cy) * fy + shift;
        for (let i = 0; i < cols; i += 1) {
          const x = i * CELL;
          const nx = (x - cx) * fx;
          let v = view.relief * (noise(nx, ny, z) + 0.45 * noise(nx * 2.1, ny * 2.1, z * 1.4 + 31));
          if (pointer.strength > 0.001) {
            // Near the cursor the terrain gives way to an even summit: clean rings
            // around the pointer that dissolve back into the map further out
            const dx = x - pointer.x;
            const dy = y - pointer.y;
            const d2 = dx * dx + dy * dy;
            const weight = pointer.strength * Math.exp(-d2 / radius2);
            v += (SUMMIT - Math.sqrt(d2) / RING_SPREAD - v) * weight;
          }
          field[j * cols + i] = v;
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const thin = new Path2D();
      const heavy = new Path2D();

      for (let j = 0; j < rows - 1; j += 1) {
        const y = j * CELL;
        for (let i = 0; i < cols - 1; i += 1) {
          const a = field[j * cols + i];           // top-left
          const b = field[j * cols + i + 1];       // top-right
          const c = field[(j + 1) * cols + i + 1]; // bottom-right
          const d = field[(j + 1) * cols + i];     // bottom-left
          // Only the levels between this cell's lowest and highest corner can cross it
          const first = Math.max(0, Math.ceil((Math.min(a, b, c, d) - LOW) / STEP));
          const final = Math.min(LEVELS, Math.floor((Math.max(a, b, c, d) - LOW) / STEP));
          const x = i * CELL;
          for (let k = first; k <= final; k += 1) {
            const level = LOW + k * STEP;
            const index = (a > level ? 8 : 0) | (b > level ? 4 : 0) | (c > level ? 2 : 0) | (d > level ? 1 : 0);
            const edges = SEGMENTS[index];
            const path = k % 5 === 0 ? heavy : thin;
            for (let s = 0; s < edges.length; s += 2) {
              const [x1, y1] = crossing(edges[s], x, y, level, a, b, c, d);
              const [x2, y2] = crossing(edges[s + 1], x, y, level, a, b, c, d);
              path.moveTo(x1, y1);
              path.lineTo(x2, y2);
            }
          }
        }
      }

      ctx.globalAlpha = Math.max(0, Math.min(1, view.ink));
      ctx.lineWidth = 1;
      ctx.strokeStyle = colours.line;
      ctx.stroke(thin);
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = colours.index;
      ctx.stroke(heavy);
    };

    const tick = (now) => {
      frame = requestAnimationFrame(tick);
      if (!visible) return;
      const nextScroll = follow ? window.scrollY : 0;
      const pointerMoving = Math.abs(pointer.target - pointer.strength) > 0.005
        || (pointer.strength > 0.01 && Math.abs(pointer.tx - pointer.x) + Math.abs(pointer.ty - pointer.y) > 0.5);
      const busy = nextScroll !== scrollY || pointerMoving || reshaping;
      if (now - last < (busy ? BUSY_MS : IDLE_MS)) return;
      const elapsed = last ? Math.min(now - last, 100) : IDLE_MS;
      last = now;
      scrollY = nextScroll;
      reshaping = settle(false);
      time += elapsed * DRIFT;
      pointer.x += (pointer.tx - pointer.x) * 0.12;
      pointer.y += (pointer.ty - pointer.y) * 0.12;
      pointer.strength += (pointer.target - pointer.strength) * 0.06;
      sample();
      draw();
    };

    const onPointerMove = (event) => {
      if (event.pointerType !== 'mouse') return;
      const rect = canvas.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      pointer.target = inside ? 1 : 0;
      if (!inside) return;
      pointer.tx = event.clientX - rect.left;
      pointer.ty = event.clientY - rect.top;
      // First contact: start the rise at the cursor rather than sliding in from a corner
      if (pointer.strength < 0.01) { pointer.x = pointer.tx; pointer.y = pointer.ty; }
    };
    const onPointerLeave = () => { pointer.target = 0; };

    resize();
    measure();
    scrollY = follow ? window.scrollY : 0;
    settle(true);
    sample();
    draw();

    const resizeObserver = new ResizeObserver(() => { resize(); measure(); sample(); draw(); });
    resizeObserver.observe(canvas);

    if (still) return () => resizeObserver.disconnect();

    // Sections move when content above them grows (filters, "show more", late images)
    const pageObserver = new ResizeObserver(measure);
    if (follow) pageObserver.observe(document.body);

    // Only animate while the map is on screen
    const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; });
    io.observe(canvas);
    frame = requestAnimationFrame(tick);
    if (interactive && finePointer) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      document.documentElement.addEventListener('pointerleave', onPointerLeave);
    }

    return () => {
      cancelAnimationFrame(frame);
      io.disconnect();
      resizeObserver.disconnect();
      pageObserver.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [tone, interactive, sections]);

  return <canvas ref={canvasRef} className={`contour-field ${className}`} aria-hidden="true" />;
}
